import {
  Button,
  IconCircle,
  IconDocument,
  OptionInterface,
  Select,
  SelectOption,
  Table,
  TablePagination,
  TablePreferences,
  Tabs,
} from "@veneer/core";

import "../../assets/css/views/checkInVsAbsence/checkInVsAbsence.css";

import { useTranslation } from "react-i18next";
import { UseCheckInVsAbsence } from "../../hooks/checkInVsAbsence/useCheckInVsAbsence";
import { UseCheckInVsAbsenceSummary } from "../../hooks/checkInVsAbsence/useCheckInVsAbsenceSummary";
import { useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../app/store";
import LoaderModal from "../../components/Loader/loaderModal";
import SimpleMessageModalWithOpenClose from "../../components/Modals/SimpleMessageModalWithOpenClose";
import { IModalMessage } from "../../models/dto/generalApi/ICheckInVsAbsence";

export default function CheckInVsAbsence() {
  const [t] = useTranslation("global");
  const [selectedTabIdDefault, setSelectedTabIdDefault] = useState(0);
  const [selectedMonth, setSelectedMonth] = useState<number>(
    new Date().getMonth() + 1
  );
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState<number>(currentYear);
  const languageID: any = useSelector((state: RootState) => state.languageId);
  const [loadingCheckInVsAbsence, setLoadingCheckInVsAbsence] = useState(false);
  const [modalMessage, setModalMessage] = useState<IModalMessage>({
    show: false,
    message: "",
    title: "",
    onClose: function () {},
  });
  const {
    columns,
    columnsWidth,
    pagedData,
    currentPage,
    pageSize,
    loading,
    filteredMonths,
    years,
    setCurrentPage,
    setPageSize,
    tableData,
    onClickCheckInVsAbsence,
    originalData,
  } = UseCheckInVsAbsence(
    selectedMonth,
    selectedYear,
    setLoadingCheckInVsAbsence,
    setModalMessage
  );

  const {
    columnsSummary,
    columnsWidthSummary,
    tableDataSummary,
    pagedDataSummary,
    currentPageSummary,
    pageSizeSummary,
    loadingSummary,
    setCurrentPageSummary,
    setPageSizeSummary,
    filteredMonthsSummary,
    yearsSummary,
    onClickCheckInVsAbsenceSummary,
    originalDataSummary,
  } = UseCheckInVsAbsenceSummary(
    selectedMonth,
    selectedYear,
    setLoadingCheckInVsAbsence,
    setModalMessage
  );

  const legendItems = [
    {
      color: "#00D072",
      labelKey: "header.earlyCheckIn",
    },
    {
      color: "#FFC000",
      labelKey: "header.lateCheckIn",
    },
    {
      color: "#BCBABA",
      labelKey: "header.scheduledDayOff",
    },
    {
      color: "#F87C63",
      labelKey: "header.noScheduledAgendaOrAbsence",
    },
    {
      color: "#508AFD",
      labelKey: "header.justifiedAbsence",
    },
    {
      color: "#E4080A",
      labelKey: "header.unjustifiedAbsence",
    },
  ];

  const yearOptions = years.map((year) => ({
    label: year.toString(),
    value: year,
  }));

  const yearOptionsSummary = yearsSummary.map((year) => ({
    label: year.toString(),
    value: year,
  }));

  const handlePageChange = (page: number) => setCurrentPage(page);
  const handlePageChangeSummary = (page: number) => setCurrentPageSummary(page);

  const handlePageSizeChange = (
    event: React.MouseEvent<HTMLElement>,
    option: OptionInterface<number>
  ) => setPageSize(option.value);

  const handlePageSizeChangeSummary = (
    event: React.MouseEvent<HTMLElement>,
    option: OptionInterface<number>
  ) => setPageSizeSummary(option.value);

  const paginationProps: TablePagination = {
    currentPage,
    onPageChange: handlePageChange,
    onPageSizeChange: handlePageSizeChange,
    pageSize,
    totalItems: tableData.length,
  };

  const paginationPropsSummary: TablePagination = {
    currentPage: currentPageSummary,
    onPageChange: handlePageChangeSummary,
    onPageSizeChange: handlePageSizeChangeSummary,
    pageSize: pageSizeSummary,
    totalItems: tableDataSummary.length,
  };

  const tablePreferences: TablePreferences = {
    width: columnsWidth,
  };

  const tablePreferencesSummary: TablePreferences = {
    width: columnsWidthSummary,
  };

  return (
    <>
      <div className="title-container">
        <h4>{t("header.checkInVsAbsence")}</h4>
      </div>

      {loadingCheckInVsAbsence && (
        <div>
          <LoaderModal>
            <div>{t("header.generating")}</div>
          </LoaderModal>
        </div>
      )}

      <SimpleMessageModalWithOpenClose
        showModal={modalMessage.show}
        setOpenCloseModal={modalMessage.onClose}
        modalTitle={modalMessage.title}
        modalMessage={modalMessage.message}
      />

      <Tabs
        controlId="tabsCheckInVsAbsence"
        onChangeTab={setSelectedTabIdDefault}
        selectedTabId={selectedTabIdDefault}
        tabs={[
          {
            id: 0,
            label: t("modal.attendance"),
            content: (
              <div className="page-container">
                <div className="container-selects">
                  <Select
                    clearIcon={false}
                    options={filteredMonths}
                    id="selectMonth"
                    label={t("header.month")}
                    placement="bottom"
                    helperTextVisibility="auto"
                    onChange={(e: SelectOption) =>
                      setSelectedMonth(Number(e.value))
                    }
                    value={
                      selectedMonth !== undefined ? [selectedMonth] : undefined
                    }
                  />

                  <Select
                    clearIcon={false}
                    options={yearOptions}
                    id="selectYear"
                    label={t("header.year")}
                    placement="bottom"
                    helperTextVisibility="auto"
                    onChange={(e: SelectOption) =>
                      setSelectedYear(Number(e.value))
                    }
                    value={
                      selectedYear !== undefined ? [selectedYear] : undefined
                    }
                  />

                  <div className="tooltipCheckInVsAbsence">
                    <span className="tooltiptextCheckInVsAbsence">
                      {t("header.downloadCheckInVsAbsence")}
                    </span>
                    <Button
                      style={{ margin: "5px" }}
                      leadingIcon={<IconDocument />}
                      onClick={() =>
                        onClickCheckInVsAbsence(
                          selectedYear,
                          selectedMonth,
                          languageID.value
                        )
                      }
                      disabled={originalData.length === 0}
                    ></Button>
                  </div>
                </div>

                <div className="container-legend">
                  {legendItems.map(({ color, labelKey }, index) => (
                    <div key={index} className="legend-item">
                      <IconCircle filled customStyle={{ color }} size={25} />
                      <span>{t(labelKey)}</span>
                    </div>
                  ))}
                </div>

                <div className="container-table">
                  {columns.length > 0 && (
                    <Table
                      showFilters={true}
                      preferences={tablePreferences}
                      className="tableCheckInVsAbsence"
                      columnResize
                      loading={loading}
                      columns={columns}
                      data={pagedData}
                      loadingDataLength={5}
                      pagination={paginationProps}
                    />
                  )}
                </div>
              </div>
            ),
          },
          {
            id: 1,
            label: "Resumen",
            content: (
              <div className="page-container">
                <div className="container-selects">
                  <Select
                    clearIcon={false}
                    options={filteredMonthsSummary}
                    id="selectMonth"
                    label={t("header.month")}
                    placement="bottom"
                    helperTextVisibility="auto"
                    onChange={(e: SelectOption) =>
                      setSelectedMonth(Number(e.value))
                    }
                    value={
                      selectedMonth !== undefined ? [selectedMonth] : undefined
                    }
                  />

                  <Select
                    clearIcon={false}
                    options={yearOptionsSummary}
                    id="selectYear"
                    label={t("header.year")}
                    placement="bottom"
                    helperTextVisibility="auto"
                    onChange={(e: SelectOption) =>
                      setSelectedYear(Number(e.value))
                    }
                    value={
                      selectedYear !== undefined ? [selectedYear] : undefined
                    }
                  />

                  <div className="tooltipCheckInVsAbsence">
                    <span className="tooltiptextCheckInVsAbsence">
                      {t("header.downloadCheckInVsAbsence")}
                    </span>
                    <Button
                      style={{ margin: "5px" }}
                      leadingIcon={<IconDocument />}
                      onClick={() =>
                        onClickCheckInVsAbsenceSummary(
                          selectedYear,
                          selectedMonth,
                          languageID.value
                        )
                      }
                      disabled={originalDataSummary.length === 0}
                    ></Button>
                  </div>
                </div>

                <div className="container-table">
                  {columnsSummary.length > 0 && (
                    <Table
                      showFilters={true}
                      preferences={tablePreferencesSummary}
                      className="tableCheckInVsAbsence"
                      columnResize
                      loading={loadingSummary}
                      columns={columnsSummary}
                      data={pagedDataSummary}
                      loadingDataLength={5}
                      pagination={paginationPropsSummary}
                    />
                  )}
                </div>
              </div>
            ),
          },
        ]}
        type="attached"
      />
    </>
  );
}
